import {
	useFormatAmount,
	useFormatter,
	type WalletCurrency,
	type WalletLedger,
	type WalletWithTotalAmountDTO
} from '@capsicum/common';

import { type ConvertedPricesKey } from '../models/mainCurrencies';
import { type WalletFanTokenBalance, type WalletTotalBalance } from '../models/walletToken';

type FormatPriceProps = {
	amount?: number | string;
	currency?: WalletCurrency;
	isAltCurrency?: boolean;
	rate?: number | string;
};

/**
 *
 * @returns utility functions to be used to display wallet balances shown in default and alternative currencies
 */
export const useWalletFormatter = () => {
	const { formatCrypto, formatFiat } = useFormatter();
	const { formatAmount } = useFormatAmount();

	/**
	 * @param amount
	 * @param currency
	 * @returns correctly formatted balance in given currency for locale
	 */
	const formatPrice = ({ amount, currency, isAltCurrency = false, rate }: FormatPriceProps): string => {
		const price =
			currency?.type === 'crypto'
				? formatCrypto(Number(amount || 0) * Number(rate || 1), currency?.shortCode || 'CHZ')
				: formatFiat(Number(amount || 0) * Number(rate || 1), currency?.shortCode);

		return isAltCurrency ? `≈ ${price}` : price;
	};

	/**
	 * @param currency
	 * @param ledger
	 * @returns formatted fanToken balance in given currency
	 */
	const formatBalance = (currency?: WalletCurrency, ledger?: WalletLedger): WalletFanTokenBalance => {
		const { balance, lockedBalance } = ledger?.convertedPrices?.[currency?.id as ConvertedPricesKey] ?? {};
		const totalBalance = Number(balance) + Number(lockedBalance);

		return {
			balance: formatAmount(balance, { maximumFractionDigits: 2, minimumFractionDigits: 2 }),
			formattedBalance: formatPrice({ amount: balance, currency }),
			formattedLockedBalance: formatPrice({ amount: lockedBalance, currency }),
			formattedTotalBalance: formatPrice({ amount: totalBalance, currency }),
			lockedBalance: formatAmount(lockedBalance, { maximumFractionDigits: 2, minimumFractionDigits: 2 }),
			shortCode: currency?.shortCode
		};
	};

	/**
	 * @param walletData
	 * @param currency
	 * @returns formatted total balance in given currency
	 */
	const formatTotalBalance = (
		walletData?: WalletWithTotalAmountDTO,
		currency?: WalletCurrency
	): WalletTotalBalance => {
		const getTotalAmount = (data?: WalletWithTotalAmountDTO) =>
			data?.convertedPrices?.[currency!.id! as ConvertedPricesKey]?.totalAmountOfFanToken;

		return {
			formattedTotalBalance: formatPrice({ amount: getTotalAmount(walletData), currency }),
			shortCode: currency?.shortCode,
			totalAmountOfFanToken: getTotalAmount(walletData)
		};
	};

	const formatEvolutionPercentage = (evolutionPercentage?: string): string => {
		if (!evolutionPercentage) return '';

		return parseFloat(evolutionPercentage) === 0
			? `${parseInt(evolutionPercentage)}%`
			: parseFloat(evolutionPercentage) >= 0
				? `+${evolutionPercentage}%`
				: `${evolutionPercentage}%`;
	};

	return {
		formatBalance,
		formatEvolutionPercentage,
		formatPrice,
		formatTotalBalance
	};
};
